import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, MenuItem, Box, IconButton,
  FormGroup, FormControlLabel, Checkbox, Select
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { blueTextFieldStyles } from "assets/themes/textFieldStyles";
import TooltipLabel from "components/Common/TooltipLabel"; // Ensure this path is correct

const TARGET_GROUPS = [
  "Young People (18-29)",
  "People with Disabilities",
  "Long-term Unemployed",
  "Older People (65-75)",
  "Migrants & Minorities",
  "LGBTQIA+ community",
];

const countWords = (str) => {
  return str ? str.trim().split(/\s+/).filter(word => word.length > 0).length : 0;
};

const SolutionProposalDialog = ({ open, onClose, onSave, initialData, challenges, rdds, stakeholders }) => {
  const [formData, setFormData] = useState({
    challenge_id: "",
    rdd_id: "",
    description: "",
    stakeholders: [], // Now array of objects: { stakeholder_id: "", role: "" }
    target_groups: [],
    ccp_addressed: "",
    additional_support: "",
  });

  const [otherTargetGroup, setOtherTargetGroup] = useState("");
  const [isOtherChecked, setIsOtherChecked] = useState(false);

  useEffect(() => {
    if (open) {
      if (initialData) {
        setFormData(initialData);
        
        // Handle custom "Other" target group logic
        const predefined = new Set(TARGET_GROUPS);
        const customGroup = initialData.target_groups.find(g => !predefined.has(g));
        if (customGroup) {
          setIsOtherChecked(true);
          setOtherTargetGroup(customGroup);
        } else {
          setIsOtherChecked(false);
          setOtherTargetGroup("");
        }
      } else {
        setFormData({
          challenge_id: "", rdd_id: "", description: "", stakeholders: [], target_groups: [], ccp_addressed: "", additional_support: ""
        });
        setIsOtherChecked(false);
        setOtherTargetGroup("");
      }
    }
  }, [open, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "description" && countWords(value) > 200) return;
    if (name === "additional_support" && countWords(value) > 100) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChallengeChange = (e) => {
    const selectedChallengeId = e.target.value;
    const challenge = challenges.find((c) => c.id === selectedChallengeId);
    setFormData((prev) => ({
      ...prev,
      challenge_id: selectedChallengeId,
      rdd_id: challenge ? challenge.rdd_id : "", 
    }));
  };

  const handleTargetGroupChange = (group) => {
    setFormData((prev) => {
      const isSelected = prev.target_groups.includes(group);
      return {
        ...prev,
        target_groups: isSelected
          ? prev.target_groups.filter((g) => g !== group)
          : [...prev.target_groups, group],
      };
    });
  };

  const handleOtherCheckboxChange = (e) => {
    const checked = e.target.checked;
    setIsOtherChecked(checked);
    if (!checked) {
      // Remove custom group if unchecked
      setFormData((prev) => ({
        ...prev,
        target_groups: prev.target_groups.filter(g => TARGET_GROUPS.includes(g))
      }));
      setOtherTargetGroup("");
    }
  };

  const handleOtherTextChange = (e) => {
    setOtherTargetGroup(e.target.value);
  };

  // --- STAKEHOLDER ROLES DYNAMIC LIST ---
  const addStakeholderRow = () => {
    setFormData(prev => ({
      ...prev,
      stakeholders: [...prev.stakeholders, { stakeholder_id: "", role: "" }]
    }));
  };

  const removeStakeholderRow = (index) => {
    setFormData(prev => ({
      ...prev,
      stakeholders: prev.stakeholders.filter((_, i) => i !== index)
    }));
  };

  const updateStakeholderRow = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.stakeholders];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, stakeholders: updated };
    });
  };

  const handleSubmit = () => {
    // Inject the "Other" text into the target groups payload before saving
    let finalTargetGroups = formData.target_groups.filter(g => TARGET_GROUPS.includes(g));
    if (isOtherChecked && otherTargetGroup.trim() !== "") {
      finalTargetGroups.push(otherTargetGroup.trim());
    }

    onSave({
      ...formData,
      target_groups: finalTargetGroups
    });
  };

  const selectedRddName = rdds.find(r => r.id === formData.rdd_id)?.name || "Select a challenge first";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold", color: "#23576B" }}>
        {initialData ? "View / Edit Solution Proposal" : "Add Solution Proposal"}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
          
          <Box>
            <TooltipLabel 
              label="Challenge Addressed" 
              tooltip="Select the territorial challenge from LW2 that this solution aims to address." 
            />
            <Select fullWidth name="challenge_id" value={formData.challenge_id} onChange={handleChallengeChange} displayEmpty sx={blueTextFieldStyles}>
              <MenuItem value="" disabled>Select a challenge</MenuItem>
              {challenges.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <TooltipLabel label="RDD (Auto-filled)" tooltip="The Rural Development Domain associated with the selected challenge." />
            <TextField fullWidth disabled value={selectedRddName} sx={blueTextFieldStyles} />
          </Box>

          <Box>
            <TooltipLabel 
              label="Solution's Proposal Title & Description (Max 200 words)" 
              tooltip="Describe briefly the solution proposal as emerging from the group discussion" 
            />
            <TextField fullWidth multiline rows={4} name="description" value={formData.description} onChange={handleChange} sx={blueTextFieldStyles} helperText={`${countWords(formData.description)} / 200 words`} />
          </Box>

          {/* DYNAMIC STAKEHOLDER LIST */}
          <Box>
            <TooltipLabel 
              label="RIE Stakeholders & Potential Roles" 
              tooltip="List the stakeholders, including their name/organizations that expressed willingness to potentially participate in the implementation of the solution and the role they potentially want to take on." 
            />
            {formData.stakeholders.map((stRow, index) => (
              <Box key={index} sx={{ display: "flex", gap: 2, mb: 2, alignItems: "flex-start" }}>
                <Box sx={{ flex: 1 }}>
                  <Select
                    fullWidth
                    displayEmpty
                    value={stRow.stakeholder_id}
                    onChange={(e) => updateStakeholderRow(index, "stakeholder_id", e.target.value)}
                    sx={blueTextFieldStyles}
                  >
                    <MenuItem value="" disabled>Select Stakeholder</MenuItem>
                    {stakeholders.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{`${s.first_name} ${s.last_name} ${s.organization ? `(${s.organization})` : ""}`}</MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ flex: 1.5 }}>
                  <TextField 
                    fullWidth 
                    placeholder="Describe the role they potentially want to take on" 
                    value={stRow.role}
                    onChange={(e) => updateStakeholderRow(index, "role", e.target.value)}
                    sx={blueTextFieldStyles}
                  />
                </Box>
                <IconButton onClick={() => removeStakeholderRow(index)} sx={{ color: "#d32f2f", mt: 0.5 }}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={addStakeholderRow} sx={{ textTransform: "none", fontWeight: 600, color: "#13A89E" }}>
              Add Stakeholder Role
            </Button>
          </Box>

          <Box>
            <TooltipLabel 
              label="Groups at Risk of Exclusion" 
              tooltip="Is the solution involving one or more of the RURACTIVE groups at risk of social exclusion and underrepresentation that you are collaborating with in your RIE." 
            />
            <FormGroup row>
              {TARGET_GROUPS.map((group) => (
                <FormControlLabel 
                  key={group} 
                  control={<Checkbox checked={formData.target_groups.includes(group)} onChange={() => handleTargetGroupChange(group)} sx={{ color: "#23576B", '&.Mui-checked': { color: "#13A89E" } }} />} 
                  label={group} 
                />
              ))}
              <FormControlLabel 
                control={<Checkbox checked={isOtherChecked} onChange={handleOtherCheckboxChange} sx={{ color: "#23576B", '&.Mui-checked': { color: "#13A89E" } }} />} 
                label="Other (please specify)" 
              />
            </FormGroup>
            {isOtherChecked && (
              <TextField 
                fullWidth 
                placeholder="Type other target group here..." 
                value={otherTargetGroup} 
                onChange={handleOtherTextChange} 
                sx={{ ...blueTextFieldStyles, mt: 1 }} 
              />
            )}
          </Box>

          <Box>
            <TooltipLabel 
              label="Cross-cutting Priorities" 
              tooltip="Which crosscutting priorities is the solution proposal addressing (try to address them all and try to briefly describe how)?" 
            />
            <TextField fullWidth multiline rows={3} name="ccp_addressed" value={formData.ccp_addressed} onChange={handleChange} placeholder="Briefly describe how..." sx={blueTextFieldStyles} />
          </Box>

          <Box>
            <TooltipLabel 
              label="Additional Support Needed (Max 100 words)" 
              tooltip="Describe briefly or list what additional support would be needed as emerging from the group discussion" 
            />
            <TextField fullWidth multiline rows={3} name="additional_support" value={formData.additional_support} onChange={handleChange} sx={blueTextFieldStyles} helperText={`${countWords(formData.additional_support)} / 100 words`} />
          </Box>

        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, px: 3 }}>
        <Button onClick={onClose} sx={{ color: "#667085", fontWeight: 600 }}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!formData.challenge_id} sx={{ backgroundColor: "#23576B", color: "#fff", '&:hover': { backgroundColor: "#1B4454" } }}>
          Save Proposal
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SolutionProposalDialog;